import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Wallet, Info, TrendingDown, IndianRupee, Calendar, ArrowDownCircle } from 'lucide-react';

const LiveSWPAnalysisPage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const [inputs, setInputs] = useState({
    initial_investment: 5000000,
    investment_date: '2020-01-01',
    withdrawal_percent: 8,
    withdrawal_start_date: '2021-01-01',
    scheme_return: 10.0
  });
  
  const [result, setResult] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const calculateSWP = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/research/swp-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inputs)
      });
      const data = await res.json();
      if (data.status === 'success') {
        setResult(data.data);
      }
    } catch (error) {
      console.error('Error calculating SWP:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl mb-4">
              <Wallet className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Live SWP Analysis
            </h1>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  Systematic Withdrawal Plan (SWP) allows you to withdraw a fixed amount regularly from your mutual fund investment.
                  Analyze how your corpus would sustain with regular withdrawals.
                </p>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-orange-400" />
                SWP Details
              </h2>

              {/* AMC & Category Filters */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Fund House</label>
                  <select
                    value={selectedAmc}
                    onChange={(e) => setSelectedAmc(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-orange-500 appearance-none cursor-pointer text-sm"
                  >
                    <option value="all" className="bg-gray-900">All Fund Houses</option>
                    {amcList.map((amc, idx) => (
                      <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-white/70 text-sm mb-2 block">Scheme Type</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-orange-500 appearance-none cursor-pointer text-sm"
                  >
                    {categories.map((cat, idx) => (
                      <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-6">
                {/* Initial Investment */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-white font-medium">Initial Investment</label>
                    <span className="text-orange-400 font-bold text-lg">{formatCurrency(inputs.initial_investment)}</span>
                  </div>
                  <input
                    type="range"
                    min="100000"
                    max="50000000"
                    step="100000"
                    value={inputs.initial_investment}
                    onChange={(e) => setInputs({...inputs, initial_investment: parseInt(e.target.value)})}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>₹1 L</span>
                    <span>₹5 Cr</span>
                  </div>
                </div>

                {/* Investment Date */}
                <div>
                  <label className="text-white font-medium mb-2 block">Investment Date</label>
                  <input
                    type="date"
                    value={inputs.investment_date}
                    onChange={(e) => setInputs({...inputs, investment_date: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Withdrawal Start Date */}
                <div>
                  <label className="text-white font-medium mb-2 block">Withdrawal Start Date</label>
                  <input
                    type="date"
                    value={inputs.withdrawal_start_date}
                    onChange={(e) => setInputs({...inputs, withdrawal_start_date: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-orange-500"
                    max={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Withdrawal Rate */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-white font-medium">Annual Withdrawal Rate</label>
                    <span className="text-red-400 font-bold text-lg">{inputs.withdrawal_percent}%</span>
                  </div>
                  <input
                    type="range"
                    min="4"
                    max="15"
                    step="0.5"
                    value={inputs.withdrawal_percent}
                    onChange={(e) => setInputs({...inputs, withdrawal_percent: parseFloat(e.target.value)})}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>4% (Safe)</span>
                    <span>15% (Aggressive)</span>
                  </div>
                </div>

                {/* Expected Return */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-white font-medium">Expected Return (CAGR)</label>
                    <span className="text-green-400 font-bold text-lg">{inputs.scheme_return}%</span>
                  </div>
                  <input
                    type="range"
                    min="6"
                    max="15"
                    step="0.5"
                    value={inputs.scheme_return}
                    onChange={(e) => setInputs({...inputs, scheme_return: parseFloat(e.target.value)})}
                    className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>6%</span>
                    <span>15%</span>
                  </div>
                </div>

                <button
                  onClick={calculateSWP}
                  disabled={loading}
                  className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(249,115,22,0.4)] transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? 'Calculating...' : 'Calculate SWP Analysis'}
                </button>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-6">
              {result ? (
                <>
                  {/* Main Result */}
                  <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-xl rounded-3xl p-8 border border-orange-500/30">
                    <p className="text-white/60 text-sm mb-2">Remaining Corpus</p>
                    <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.remaining_corpus)}</p>
                    <p className={`text-lg ${result.remaining_corpus > 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {result.remaining_corpus > 0 ? 'Corpus is sustainable' : 'Corpus depleted'}
                    </p>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <IndianRupee className="w-4 h-4 text-white/50" />
                        <p className="text-white/50 text-xs">Monthly Withdrawal</p>
                      </div>
                      <p className="text-2xl font-bold text-orange-400">{formatCurrency(result.monthly_withdrawal)}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowDownCircle className="w-4 h-4 text-red-400" />
                        <p className="text-white/50 text-xs">Total Withdrawn</p>
                      </div>
                      <p className="text-2xl font-bold text-red-400">{formatCurrency(result.total_withdrawn)}</p>
                    </div>
                  </div>

                  {/* Corpus Journey */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <p className="text-white font-semibold mb-4">Corpus Journey</p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <div className="flex-1">
                          <p className="text-white/60 text-sm">Initial Investment</p>
                          <p className="text-white font-semibold">{formatCurrency(result.initial_investment)}</p>
                        </div>
                      </div>
                      <div className="w-0.5 h-6 bg-white/10 ml-1.5"></div>
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <div className="flex-1">
                          <p className="text-white/60 text-sm">Corpus at Withdrawal Start</p>
                          <p className="text-green-400 font-semibold">{formatCurrency(result.corpus_at_withdrawal_start)}</p>
                        </div>
                      </div>
                      <div className="w-0.5 h-6 bg-white/10 ml-1.5"></div>
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                        <div className="flex-1">
                          <p className="text-white/60 text-sm">Current Remaining Corpus</p>
                          <p className="text-orange-400 font-semibold">{formatCurrency(result.remaining_corpus)}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Summary Table */}
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <p className="text-white font-semibold mb-4">Analysis Summary</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Initial Investment</span>
                        <span className="text-white font-medium">{formatCurrency(result.initial_investment)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Growth Before Withdrawal</span>
                        <span className="text-green-400 font-medium">+{formatCurrency(result.corpus_at_withdrawal_start - result.initial_investment)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Withdrawal Period</span>
                        <span className="text-white font-medium">{result.withdrawal_months} months</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-white/10">
                        <span className="text-white/60">Total Amount Withdrawn</span>
                        <span className="text-red-400 font-medium">-{formatCurrency(result.total_withdrawn)}</span>
                      </div>
                      <div className="flex justify-between py-2">
                        <span className="text-white font-medium">Remaining Corpus</span>
                        <span className="text-orange-400 font-bold text-lg">{formatCurrency(result.remaining_corpus)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Advice */}
                  <div className={`backdrop-blur-xl rounded-2xl p-6 border ${
                    inputs.withdrawal_percent > inputs.scheme_return 
                      ? 'bg-red-500/10 border-red-500/30' 
                      : 'bg-green-500/10 border-green-500/30'
                  }`}>
                    <p className={`font-semibold mb-2 ${
                      inputs.withdrawal_percent > inputs.scheme_return ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {inputs.withdrawal_percent > inputs.scheme_return ? '⚠️ Warning' : '✅ Sustainable Plan'}
                    </p>
                    <p className="text-white/80 text-sm">
                      {inputs.withdrawal_percent > inputs.scheme_return 
                        ? `Your withdrawal rate (${inputs.withdrawal_percent}%) exceeds expected returns (${inputs.scheme_return}%). Your corpus will deplete over time. Consider reducing withdrawals.`
                        : `Your withdrawal rate (${inputs.withdrawal_percent}%) is lower than expected returns (${inputs.scheme_return}%). Your corpus should sustain and even grow over time.`
                      }
                    </p>
                  </div>
                </>
              ) : (
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 text-center">
                  <Wallet className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">Enter SWP details and click Calculate to see your withdrawal analysis</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LiveSWPAnalysisPage;
